import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Chip,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Avatar,
  InputAdornment,
  Snackbar,
  FormControlLabel,
  Checkbox,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Verified as VerifiedIcon,
  Google as GoogleIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Email as EmailIcon,
  MarkEmailUnread as MarkEmailUnreadIcon,
  ContentCopy as ContentCopyIcon,
  FileDownload as FileDownloadIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridColumnVisibilityModel, GridPaginationModel } from '@mui/x-data-grid';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { User, UserFilters } from '../types/user';
import ResponsiveDialog from '../components/ResponsiveDialog';
import PageHeader from '../components/PageHeader';
import { useIsMobileMd } from '../hooks/useIsMobile';
const USER_DELETE_PREVIEW_LABELS: Record<string, string> = {
  owned_announcements: 'Annonces du compte',
  owned_announcements_active: 'Dont annonces actives',
  announcement_images_on_disk: 'Images annonces (fichiers sur le serveur)',
  conversations: 'Conversations',
  messages: 'Messages',
  matchings: 'Matchings',
  favorites: 'Favoris',
  has_avatar: 'Avatar (fichier sur le serveur)',
};

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobileMd();
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    status: '',
    role: '',
    never_logged_in: '',
    page: 1,
    limit: 20,
  });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [actionsMenu, setActionsMenu] = useState<{ anchor: HTMLElement; user: User } | null>(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  const [exportStatus, setExportStatus] = useState<'active' | 'inactive' | ''>('');
  const [exportNeverLoggedIn, setExportNeverLoggedIn] = useState(false);
  const [exportEmails, setExportEmails] = useState('');
  const [exportCount, setExportCount] = useState<number | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    setColumnVisibilityModel({
      roles: !isMobile,
      managed_location_providers: !isMobile,
      isVerified: !isMobile,
      authProvider: !isMobile,
      createdAt: !isMobile,
      isOnline: !isMobile,
      lastLoginAt: !isMobile,
    });
  }, [isMobile]);

  // Requête pour récupérer les utilisateurs
  const { data: usersData, isFetching, error } = useQuery({
    queryKey: ['users', filters],
    queryFn: () =>
      apiService.getUsers({
        ...filters,
        never_logged_in: filters.never_logged_in ? 1 : undefined,
      }),
    select: (response) => response.data,
    placeholderData: keepPreviousData,
  });

  const rowCount = usersData?.pagination?.total ?? 0;

  // Mutation pour basculer le statut d'un utilisateur
  const toggleStatusMutation = useMutation({
    mutationFn: (userId: number) => apiService.toggleUserStatus(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const { data: userDeletePreview, isLoading: userDeletePreviewLoading } = useQuery({
    queryKey: ['user-delete-preview', userToDelete?.id],
    queryFn: () => apiService.getUserDeletionPreview(userToDelete!.id),
    enabled: Boolean(deleteDialogOpen && userToDelete?.id),
  });

  // Mutation pour supprimer un utilisateur
  const deleteUserMutation = useMutation({
    mutationFn: (userId: number) => apiService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleteError(null);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    },
    onError: (err: unknown) => {
      let message = 'Échec de la suppression.';
      if (err && typeof err === 'object' && 'response' in err) {
        const data = (err as { response?: { data?: { error?: string } } }).response?.data;
        if (typeof data?.error === 'string' && data.error.length > 0) {
          message = data.error;
        }
      }
      setDeleteError(message);
    },
  });

  const resendVerificationMutation = useMutation({
    mutationFn: (userId: number) => apiService.resendUserVerificationEmail(userId),
    onSuccess: (response) => {
      const message =
        typeof response.data?.message === 'string'
          ? response.data.message
          : 'Email de vérification renvoyé.';
      setSnackbar({ open: true, message, severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: unknown) => {
      let message = 'Échec du renvoi de l’email de vérification.';
      if (err && typeof err === 'object' && 'response' in err) {
        const data = (err as { response?: { data?: { error?: string } } }).response?.data;
        if (typeof data?.error === 'string' && data.error.length > 0) {
          message = data.error;
        }
      }
      setSnackbar({ open: true, message, severity: 'error' });
    },
  });

  const verifyUserMutation = useMutation({
    mutationFn: (userId: number) => apiService.verifyUser(userId),
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Utilisateur marqué comme vérifié.', severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: unknown) => {
      let message = 'Échec de la vérification manuelle.';
      if (err && typeof err === 'object' && 'response' in err) {
        const data = (err as { response?: { data?: { error?: string } } }).response?.data;
        if (typeof data?.error === 'string' && data.error.length > 0) {
          message = data.error;
        }
      }
      setSnackbar({ open: true, message, severity: 'error' });
    },
  });

  const handleFilterChange = (field: keyof UserFilters, value: UserFilters[keyof UserFilters]) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1,
    }));
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    const nextPage = model.pageSize !== (filters.limit || 20) ? 1 : model.page + 1;
    const nextLimit = model.pageSize;
    if (nextPage === filters.page && nextLimit === filters.limit) {
      return;
    }
    setFilters((prev) => ({
      ...prev,
      page: nextPage,
      limit: nextLimit,
    }));
  };

  const handleExportEmails = async () => {
    if (!exportDateFrom && !exportDateTo) {
      setExportError('Indiquez au moins une date de début ou de fin.');
      return;
    }
    setExportLoading(true);
    setExportError(null);
    setExportEmails('');
    setExportCount(null);
    try {
      const response = await apiService.exportUserEmails({
        date_from: exportDateFrom || undefined,
        date_to: exportDateTo || undefined,
        status: exportStatus || undefined,
        never_logged_in: exportNeverLoggedIn || undefined,
      });
      setExportEmails(response.data.emails);
      setExportCount(response.data.count);
    } catch (err: unknown) {
      let message = 'Impossible d’exporter les e-mails.';
      if (err && typeof err === 'object' && 'response' in err) {
        const data = (err as { response?: { data?: { error?: string } } }).response?.data;
        if (typeof data?.error === 'string' && data.error.length > 0) {
          message = data.error;
        }
      }
      setExportError(message);
    } finally {
      setExportLoading(false);
    }
  };

  const handleCopyExportedEmails = async () => {
    if (!exportEmails) {
      return;
    }
    try {
      await navigator.clipboard.writeText(exportEmails);
      setSnackbar({ open: true, message: 'Liste copiée dans le presse-papiers.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Copie impossible (presse-papiers).', severity: 'error' });
    }
  };

  const handleDownloadExportedEmails = () => {
    if (!exportEmails) {
      return;
    }
    const blob = new Blob([exportEmails], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const suffix = [exportDateFrom, exportDateTo].filter(Boolean).join('_') || 'export';
    link.href = url;
    link.download = `sherlook-emails-${suffix}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleViewDetails = (user: User) => {
    setSelectedUser(user);
    setDetailsDialogOpen(true);
  };

  const handleDeleteUser = (user: User) => {
    setDeleteError(null);
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (userToDelete) {
      deleteUserMutation.mutate(userToDelete.id);
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'error';
  };

  const getStatusIcon = (isActive: boolean) => {
    return isActive ? <CheckCircleIcon /> : <CancelIcon />;
  };

  const getAuthProviderIcon = (provider?: string) => {
    switch (provider) {
      case 'google':
        return <GoogleIcon fontSize="small" />;
      case 'facebook':
        return <FacebookIcon fontSize="small" />;
      case 'instagram':
        return <InstagramIcon fontSize="small" />;
      default:
        return <EmailIcon fontSize="small" />;
    }
  };

  const getRoleLabel = (roles: string[]) => {
    // Vérifier que roles existe et est un tableau
    if (!roles || !Array.isArray(roles)) {
      return 'Utilisateur';
    }
    
    const roleLabels: string[] = [];
    
    // Trier les rôles par ordre de priorité pour un affichage cohérent
    const roleOrder = ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_LOCATION_ADMIN', 'ROLE_PROVIDER', 'ROLE_USER'];
    
    roleOrder.forEach(role => {
      if (roles.includes(role)) {
        switch (role) {
          case 'ROLE_SUPER_ADMIN':
            roleLabels.push('Super Admin');
            break;
          case 'ROLE_ADMIN':
            roleLabels.push('Admin');
            break;
          case 'ROLE_LOCATION_ADMIN':
            roleLabels.push('Admin Partenaire');
            break;
          case 'ROLE_PROVIDER':
            roleLabels.push('Partenaire-utilisateur');
            break;
          case 'ROLE_USER':
            roleLabels.push('Utilisateur');
            break;
        }
      }
    });
    
    return roleLabels.length > 0 ? roleLabels.join(' - ') : 'Utilisateur';
  };

  const formatLastLogin = (value?: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeActivity = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60_000);
    if (diffMin < 1) return "À l'instant";
    if (diffMin < 60) return `Il y a ${diffMin} min`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `Il y a ${diffHours} h`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `Il y a ${diffDays} j`;
    return formatLastLogin(value);
  };

  const getRoleColor = (roles: string[]) => {
    // Vérifier que roles existe et est un tableau
    if (!roles || !Array.isArray(roles)) {
      return 'default';
    }
    
    // Retourner la couleur du rôle le plus élevé
    if (roles.includes('ROLE_SUPER_ADMIN')) return 'error';
    if (roles.includes('ROLE_ADMIN')) return 'warning';
    if (roles.includes('ROLE_LOCATION_ADMIN')) return 'info';
    if (roles.includes('ROLE_PROVIDER')) return 'secondary';
    return 'default';
  };

  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: 'Utilisateur',
      flex: 1,
      minWidth: isMobile ? 160 : 220,
      renderCell: (params) => (
        <Box 
          display="flex" 
          alignItems="center" 
          gap={1.5}
          minWidth={0}
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate(`/users/${params.row.id}`)}
        >
          <Avatar 
            src={params.row.avatarUrl}
            sx={{ 
              bgcolor: params.row.avatarUrl ? 'transparent' : 'primary.light', 
              width: 32, 
              height: 32,
              flexShrink: 0,
              border: params.row.avatarUrl ? '1px solid' : 'none',
              borderColor: params.row.avatarUrl ? 'divider' : 'transparent',
            }}
          >
            {!params.row.avatarUrl && <PersonIcon fontSize="small" />}
          </Avatar>
          <Box minWidth={0}>
            <Typography variant="subtitle2" fontWeight="bold" noWrap title={params.row.fullName || params.value}>
              {params.row.fullName || params.value}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap title={params.value}>
              {params.value}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'roles',
      headerName: 'Rôle',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={getRoleLabel(params.value)}
          size="small"
          color={getRoleColor(params.value) as any}
          variant="outlined"
        />
      ),
    },
    {
      field: 'managed_location_providers',
      headerName: 'Partenaire(s) géré(s)',
      minWidth: 220,
      flex: 0.35,
      sortable: false,
      renderCell: (params) => {
        const roles: string[] = params.row.roles ?? [];
        const isProviderScope =
          roles.includes('ROLE_LOCATION_ADMIN') || roles.includes('ROLE_PROVIDER');
        if (!isProviderScope) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        const list = (params.row.managed_location_providers ?? []) as { id: number; name: string }[];
        if (list.length === 0) {
          return (
            <Typography variant="caption" color="text.secondary">
              Aucun
            </Typography>
          );
        }
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5, alignItems: 'center' }}>
            {list.map((p) => (
              <Chip
                key={p.id}
                label={p.name || `ID ${p.id}`}
                size="small"
                variant="outlined"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/providers/${p.id}`);
                }}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: 'isActive',
      headerName: 'Statut',
      width: isMobile ? 120 : 240,
      minWidth: isMobile ? 100 : 160,
      renderCell: (params) => {
        const isActive = Boolean(params.value);
        const sensitive = params.row.inactive_sensitive_content === true;
        const label = isActive ? 'Actif' : sensitive ? 'Inactif (contenu sensible)' : 'Inactif';
        return (
          <Chip
            label={isMobile ? (isActive ? 'Actif' : 'Inactif') : label}
            size="small"
            color={getStatusColor(isActive) as any}
            icon={getStatusIcon(isActive)}
          />
        );
      },
    },
    {
      field: 'isVerified',
      headerName: 'Vérifié',
      width: 100,
      renderCell: (params) => (
        params.value ? (
          <Chip
            label="Oui"
            size="small"
            color="success"
            icon={<VerifiedIcon />}
          />
        ) : (
          <Chip
            label="Non"
            size="small"
            color="warning"
            icon={<WarningIcon />}
          />
        )
      ),
    },
    {
      field: 'authProvider',
      headerName: 'Connexion',
      width: 120,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          {getAuthProviderIcon(params.value)}
          <Typography variant="caption">
            {params.value || 'email'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Inscription',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString('fr-FR')}
        </Typography>
      ),
    },
    {
      field: 'isOnline',
      headerName: 'En ligne',
      width: 120,
      renderCell: (params) => {
        const online = Boolean(params.value);
        const activityLabel = formatRelativeActivity(
          (params.row.lastActivityAt as string | null | undefined)
            ?? (params.row.lastLoginAt as string | null | undefined),
        );
        return (
          <Box>
            <Chip
              label={online ? 'Oui' : 'Non'}
              size="small"
              color={online ? 'success' : 'default'}
              variant={online ? 'filled' : 'outlined'}
            />
            {activityLabel ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                {activityLabel}
              </Typography>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: 'lastLoginAt',
      headerName: 'Dernière connexion',
      width: 170,
      renderCell: (params) => (
        <Typography variant="body2" color={params.value ? 'text.primary' : 'text.secondary'}>
          {formatLastLogin(params.value as string | null | undefined)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 72,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const user = params.row as User;
        return (
          <IconButton
            size="medium"
            aria-label="Actions utilisateur"
            onClick={(e) => {
              e.stopPropagation();
              setActionsMenu({ anchor: e.currentTarget, user });
            }}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <MoreVertIcon />
          </IconButton>
        );
      },
    },
  ];
  if (error) {
    return (
      <Box>
        <Alert severity="error">
          Erreur lors du chargement des utilisateurs
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={
          <Typography variant="h4" component="h1">
            Gestion des Utilisateurs
          </Typography>
        }
      />

      {/* Filtres */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Rechercher"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Statut"
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="active">Actifs</MenuItem>
                  <MenuItem value="inactive">Inactifs</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Rôle</InputLabel>
                <Select
                  value={filters.role}
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                  label="Rôle"
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="ROLE_USER">Utilisateur</MenuItem>
                  <MenuItem value="ROLE_PROVIDER">Partenaire-utilisateur</MenuItem>
                  <MenuItem value="ROLE_LOCATION_ADMIN">Admin Partenaire</MenuItem>
                  <MenuItem value="ROLE_ADMIN">Admin</MenuItem>
                  <MenuItem value="ROLE_SUPER_ADMIN">Super Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(filters.never_logged_in)}
                    onChange={(e) => handleFilterChange('never_logged_in', e.target.checked ? true : '')}
                  />
                }
                label="Jamais connectés"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Export e-mails par période d'inscription */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Export des e-mails (inscriptions)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Liste séparée par des virgules, selon la date de création du compte. Les comptes internes sont exclus.
            « Jamais connectés » exclut aussi les comptes déjà liés à Google.
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Inscrits depuis le"
                type="date"
                value={exportDateFrom}
                onChange={(e) => setExportDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Inscrits jusqu'au"
                type="date"
                value={exportDateTo}
                onChange={(e) => setExportDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Statut (optionnel)</InputLabel>
                <Select
                  value={exportStatus}
                  onChange={(e) => setExportStatus(e.target.value as 'active' | 'inactive' | '')}
                  label="Statut (optionnel)"
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="active">Actifs</MenuItem>
                  <MenuItem value="inactive">Inactifs</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exportNeverLoggedIn}
                    onChange={(e) => setExportNeverLoggedIn(e.target.checked)}
                  />
                }
                label="Jamais connectés"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => void handleExportEmails()}
                disabled={exportLoading}
                startIcon={exportLoading ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}
              >
                {exportLoading ? 'Export…' : 'Exporter'}
              </Button>
            </Grid>
          </Grid>
          {exportError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {exportError}
            </Alert>
          ) : null}
          {exportCount !== null ? (
            <Box sx={{ mt: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
                <Typography variant="body2" color="text.secondary">
                  {exportCount} adresse{exportCount > 1 ? 's' : ''} e-mail
                </Typography>
                <Box display="flex" gap={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => void handleCopyExportedEmails()}
                    disabled={!exportEmails}
                  >
                    Copier
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleDownloadExportedEmails}
                    disabled={!exportEmails}
                  >
                    Télécharger .txt
                  </Button>
                </Box>
              </Box>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
                value={exportEmails}
                InputProps={{ readOnly: true }}
                placeholder="Aucun e-mail pour cette période."
              />
            </Box>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ px: { xs: 1, sm: 2 }, maxWidth: '100%' }}>
          <Box sx={{ height: { xs: 520, sm: 600 }, width: '100%', maxWidth: '100%' }}>
            <DataGrid
              rows={usersData?.users || []}
              columns={columns}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={setColumnVisibilityModel}
              loading={isFetching}
              pageSizeOptions={[10, 20, 50]}
              pagination
              paginationModel={{
                page: (filters.page || 1) - 1,
                pageSize: filters.limit || 20,
              }}
              onPaginationModelChange={handlePaginationModelChange}
              rowCount={rowCount}
              paginationMode="server"
              disableRowSelectionOnClick
              rowHeight={52}
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': { outline: 'none' },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Menu
        anchorEl={actionsMenu?.anchor ?? null}
        open={Boolean(actionsMenu)}
        onClose={() => setActionsMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {actionsMenu?.user ? (
          [
            <MenuItem
              key="view"
              onClick={() => {
                const user = actionsMenu.user;
                setActionsMenu(null);
                handleViewDetails(user);
              }}
            >
              <ListItemIcon>
                <VisibilityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Voir</ListItemText>
            </MenuItem>,
            <MenuItem
              key="verify"
              disabled={Boolean(actionsMenu.user.isVerified) || verifyUserMutation.isPending}
              onClick={() => {
                const id = actionsMenu.user.id;
                setActionsMenu(null);
                verifyUserMutation.mutate(id);
              }}
            >
              <ListItemIcon>
                <VerifiedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Vérifier</ListItemText>
            </MenuItem>,
            <MenuItem
              key="mail"
              disabled={Boolean(actionsMenu.user.isVerified) || resendVerificationMutation.isPending}
              onClick={() => {
                const id = actionsMenu.user.id;
                setActionsMenu(null);
                resendVerificationMutation.mutate(id);
              }}
            >
              <ListItemIcon>
                <MarkEmailUnreadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Mail de vérification</ListItemText>
            </MenuItem>,
            <MenuItem
              key="toggle"
              onClick={() => {
                const id = actionsMenu.user.id;
                setActionsMenu(null);
                toggleStatusMutation.mutate(id);
              }}
            >
              <ListItemIcon>
                {actionsMenu.user.isActive ? (
                  <CancelIcon fontSize="small" color="error" />
                ) : (
                  <CheckCircleIcon fontSize="small" color="success" />
                )}
              </ListItemIcon>
              <ListItemText>{actionsMenu.user.isActive ? 'Désactiver' : 'Activer'}</ListItemText>
            </MenuItem>,
            <MenuItem
              key="delete"
              onClick={() => {
                const user = actionsMenu.user;
                setActionsMenu(null);
                handleDeleteUser(user);
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Supprimer</ListItemText>
            </MenuItem>,
          ]
        ) : null}
      </Menu>

      {/* Dialog de détails */}
      <ResponsiveDialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md">
        <DialogTitle>
          Détails de l'utilisateur
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box textAlign="center">
                    <Avatar
                      src={selectedUser.avatarUrl}
                      sx={{ width: 100, height: 100, mx: 'auto', mb: 2 }}
                    >
                      <PersonIcon fontSize="large" />
                    </Avatar>
                    <Typography variant="h6">{selectedUser.fullName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedUser.email}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Statut
                      </Typography>
                      <Chip
                        label={
                          selectedUser.isActive
                            ? 'Actif'
                            : selectedUser.inactive_sensitive_content
                              ? 'Inactif (contenu sensible)'
                              : 'Inactif'
                        }
                        color={getStatusColor(selectedUser.isActive) as any}
                        icon={getStatusIcon(selectedUser.isActive)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Vérifié
                      </Typography>
                      <Chip
                        label={selectedUser.isVerified ? 'Oui' : 'Non'}
                        color={selectedUser.isVerified ? 'success' : 'warning'}
                        icon={selectedUser.isVerified ? <VerifiedIcon /> : <WarningIcon />}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Rôle
                      </Typography>
                      <Chip
                        label={getRoleLabel(selectedUser.roles)}
                        color={getRoleColor(selectedUser.roles) as any}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Connexion
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getAuthProviderIcon(selectedUser.authProvider)}
                        <Typography variant="body2">
                          {selectedUser.authProvider || 'email'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Date d'inscription
                      </Typography>
                      <Typography variant="body2">
                        {new Date(selectedUser.createdAt).toLocaleString('fr-FR')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Session
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={selectedUser.isOnline ? 'En ligne' : 'Hors ligne'}
                          size="small"
                          color={selectedUser.isOnline ? 'success' : 'default'}
                          variant={selectedUser.isOnline ? 'filled' : 'outlined'}
                        />
                        {(selectedUser.lastActivityAt || selectedUser.lastLoginAt) && (
                          <Typography variant="body2" color="text.secondary">
                            {formatRelativeActivity(selectedUser.lastActivityAt ?? selectedUser.lastLoginAt)}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Dernière connexion
                      </Typography>
                      <Typography variant="body2">
                        {formatLastLogin(selectedUser.lastLoginAt)}
                      </Typography>
                    </Grid>
                    {selectedUser.lastActivityAt ? (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Dernière activité
                        </Typography>
                        <Typography variant="body2">
                          {formatLastLogin(selectedUser.lastActivityAt)}
                        </Typography>
                      </Grid>
                    ) : null}
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>
            Fermer
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* Dialog de confirmation de suppression */}
      <ResponsiveDialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!deleteUserMutation.isPending) {
            setDeleteDialogOpen(false);
            setDeleteError(null);
          }
        }}
        maxWidth="sm"
      >
        <DialogTitle>
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Typography paragraph>
            Êtes-vous sûr de vouloir supprimer l&apos;utilisateur &quot;{userToDelete?.fullName || userToDelete?.email}&quot; ?
            Cette action est irréversible.
          </Typography>
          {userDeletePreviewLoading && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={28} />
            </Box>
          )}
          {userDeletePreview && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {userDeletePreview.warning}
              </Alert>
              <Typography variant="subtitle2" gutterBottom>
                Éléments concernés
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {Object.entries(userDeletePreview.counts).map(([key, val]) => (
                  <li key={key}>
                    <Typography variant="body2">
                      {USER_DELETE_PREVIEW_LABELS[key] ?? key} :{' '}
                      {typeof val === 'boolean' ? (val ? 'Oui' : 'Non') : val}
                    </Typography>
                  </li>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Annuler
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleteUserMutation.isPending}
          >
            {deleteUserMutation.isPending ? <CircularProgress size={20} /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersPage;
